import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './ConfigTab.module.scss'
import { TimeRangeControl } from '../../../../Insights/TimeRangeControl'
import _, { debounce, cloneDeep } from 'lodash'
import {
    DotsThreeIcon,
    ChartBarIcon,
    ChartLineUpIcon,
    TableIcon,
    ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react'
import { SORTING, SORTING_OPTIONS } from '../../../../Insights/consts'
import {
    ScoopText,
    ScoopTextField,
    ScoopSelector,
    ScoopSpinner,
    ScoopSwitch,
    ScoopCheckbox,
    ScoopButton,
    ScoopMenu,
    ScoopMenuItem,
    ScoopInfoTooltip,
} from '../../../../ui/'

export const ConfigTab = ({
    config,
    setConfig,
    chartState,
    advanced,
    handleAdvancedToggle,
    analyzeChanges,
    setAnalyzeChanges,
    columnLoading,
    setColumnLoading,
    dateFlag,
    setDateFlag,
    onOpenTimeRange,
    workspaceMetadata,
}) => {
    const controlHeight = 36
    const [availableColumns, setAvailableColumns] = useState([])
    const [chartTitle, setChartTitle] = useState(config.chartTitle)
    const [kpiCompareTarget, setKpiCompareTarget] = useState(config.kpiCompareTarget)
    const dateMenuRef = useRef()
    const [currentColumn, setCurrentColumn] = useState(null)
    const [currentTable, setCurrentTable] = useState(null)
    const [colMenuOpen, setColMenuOpen] = useState(false)

    const debouncedFunc = useCallback(
        debounce((value) => {
            const newConfig = { ...config }
            newConfig.chartTitle = value
            setConfig(newConfig)
            chartState.setChartStateConfig(newConfig)
        }, 500),
        [config, chartState]
    )
    const targetDebouncedFunc = useCallback(
        debounce((value) => {
            const newConfig = { ...config }
            newConfig.kpiCompareTarget = value
            setConfig(newConfig)
        }, 500),
        [config, chartState]
    )
    const buildSortingColumnOptions = useMemo(() => {
        if (!Array.isArray(chartState.series)) return []
        return chartState.series.reduce((acc, s) => {
            if (!['radial', 'gauge'].includes(s.type)) {
                acc.push(s.name)
            }
            return acc
        }, [])
    }, [chartState.series])

    useEffect(() => {
        if (workspaceMetadata && workspaceMetadata.inboxes) {
            const tables = []
            workspaceMetadata?.inboxes?.forEach((inbox) => {
                inbox.tables.forEach((table) => {
                    if (config.selectedTables.includes(table.reportSeriesTableID))
                        tables.push(table)
                })
            })
            const columns = tables.map((table) =>
                table.columns.map((col) => ({
                    ...col,
                    reportSeriesTableID: table.reportSeriesTableID,
                    tableName: table.tableName,
                }))
            )
            columns.forEach((tableCols) =>
                tableCols.push({
                    columnName: 'Count',
                    reportSeriesTableID: tableCols[0].reportSeriesTableID,
                    isMeasure: true,
                })
            )
            const datasetMeasures = []
            const datasetAttributes = []
            if (columns.length > 0) {
                columns.forEach((tableColumns) => {
                    tableColumns.forEach((col) => {
                        if (col.isMeasure) {
                            if (col.columnName === 'Count') {
                                if (!datasetMeasures.some((c) => c.columnName === 'Count'))
                                    datasetMeasures.push(col)
                            } else datasetMeasures.push(col)
                        } else datasetAttributes.push(col)
                    })
                })
            }
            const worksheetMeasures = config.worksheetColumns
                .filter((c) => c.isMeasure)
                .map((c) => ({
                    ...c,
                    worksheetID: config.worksheetID,
                    rangeName: config.rangeName,
                }))
            const worksheetAttributes = config.worksheetColumns.filter((c) => !c.isMeasure)
            const mixCols = [datasetAttributes, worksheetAttributes]
            setAvailableColumns([
                ...worksheetMeasures,
                ...datasetMeasures,
                ..._.intersectionBy(...mixCols.filter((a) => a.length !== 0), 'columnName'),
            ])
        }
    }, [workspaceMetadata, config.selectedTables.length, config.worksheetColumns.length])

    useEffect(() => {
        if (!kpiCompareTarget) setKpiCompareTarget(config.kpiCompareTarget)
    }, [config.kpiCompareTarget])

    const handleViewChange = (e) => {
        const value = e.target.value
        const newConfig = { ...config }
        if (value === 'table') {
            const selectedTables = []
            const selectedTableColumns = []
            const selectedTableKpis = []
            newConfig.selectedItems.forEach((item) => {
                if (
                    item.worksheetID &&
                    !selectedTableColumns.some((col) => col.measureName === item.measureName)
                ) {
                    const worksheetMetric = config.worksheetColumns.find(
                        (c) =>
                            c.columnName === item.measureName && c.worksheetID === item.worksheetID
                    )
                    const byDate = config.worksheetColumns.find((c) => c.columnType === 'DateTime')
                    if (byDate) worksheetMetric.byDate = byDate
                    if (worksheetMetric) selectedTableColumns.push(worksheetMetric)
                }
                if (item.reportSeriesTableID && !selectedTables.includes(item.reportSeriesTableID))
                    selectedTables.push(item.reportSeriesTableID)
                if (
                    item.columnName &&
                    !selectedTableColumns.some((col) => col.columnName === item.columnName)
                ) {
                    workspaceMetadata?.inboxes?.forEach((inbox) => {
                        inbox.tables.forEach((table) => {
                            table.columns.forEach((col) => {
                                if (
                                    col.columnName === item.columnName &&
                                    item.reportSeriesTableID === table.reportSeriesTableID
                                ) {
                                    selectedTableColumns.push({
                                        ...col,
                                        reportSeriesTableID: table.reportSeriesTableID,
                                    })
                                }
                            })
                        })
                    })
                }
                if (item.kpi && !selectedTableKpis.some((kpi) => kpi.metricName === item.kpi)) {
                    workspaceMetadata.kpis.forEach((kpi) => {
                        if (kpi.metricName === item.kpi) {
                            selectedTableKpis.push(kpi)
                        }
                    })
                }
            })
            newConfig.selectedTableColumns = selectedTableColumns
            newConfig.selectedTableKpis = selectedTableKpis
            newConfig.selectedTables = selectedTables
        } else if (value === 'chart') {
            if (newConfig.view === 'table') {
                let selectedItems = []
                let kpis = []
                let measures = []
                kpis = newConfig.selectedTableKpis
                    .map((k) => ({
                        kpi: k.metricName,
                        reportSeriesTableID: k.reportSeriesTableID,
                    }))
                    .slice(0, 2)
                measures = newConfig.selectedTableColumns
                    .filter((col) => col.isMeasure)
                    .map((item) => {
                        if (item.worksheetID) {
                            return {
                                measureName: item.columnName,
                                worksheetID: item.worksheetID,
                                rangeName: item.rangeName,
                            }
                        } else
                            return {
                                columnName: item.columnName,
                                reportSeriesTableID: item.reportSeriesTableID,
                            }
                    })
                    .slice(0, 2)
                selectedItems = [...kpis, ...measures]
                newConfig.selectedItems = selectedItems.slice(0, 2)
            }
        } else {
            if (newConfig.view === 'table') {
                let kpi = newConfig.selectedTableKpis.map((k) => ({
                    kpi: k.metricName,
                    reportSeriesTableID: k.reportSeriesTableID,
                }))[0]
                let measure = newConfig.selectedTableColumns
                    .filter((col) => col.isMeasure)
                    .map((item) => {
                        if (item.worksheetID) {
                            return {
                                measureName: item.columnName,
                                worksheetID: item.worksheetID,
                                rangeName: item.rangeName,
                            }
                        } else
                            return {
                                columnName: item.columnName,
                                reportSeriesTableID: item.reportSeriesTableID,
                            }
                    })[0]
                newConfig.selectedItems = kpi ? [kpi] : measure ? [measure] : []
            }
        }
        newConfig.view = value
        setConfig(newConfig)
        if (newConfig.selectedItems.length > 0 && value === 'chart') {
            chartState.getResults(newConfig)
        }
    }

    const handleColumnToggle = (col) => {
        const newConfig = { ...config }
        if (
            newConfig.selectedTableColumns.some(
                (c) =>
                    c.columnName === col.columnName &&
                    (col.reportSeriesTableID === c.reportSeriesTableID ||
                        col.worksheetID === c.worksheetID)
            )
        ) {
            if (newConfig.selectedTableColumns.length !== 1) setColumnLoading(true)
            newConfig.selectedTableColumns.splice(
                newConfig.selectedTableColumns.findIndex(
                    (c) =>
                        c.columnName === col.columnName &&
                        (col.reportSeriesTableID === c.reportSeriesTableID ||
                            col.worksheetID === c.worksheetID)
                ),
                1
            )
        } else {
            setColumnLoading(true)
            if (col.worksheetID) {
                const byDates = config.worksheetColumns.filter((c) => c.columnType === 'DateTime')
                if (byDates.length > 0)
                    newConfig.selectedTableColumns.push({
                        ...col,
                        byDate: byDates[0].columnName,
                    })
                else newConfig.selectedTableColumns.push(col)
            } else {
                newConfig.selectedTableColumns.push(col)
            }
        }
        setConfig(newConfig)
    }

    const handleSeriesTypeChange = (metricName, newSeriesType) => {
        const newConfig = cloneDeep(config)
        const oldCategoryAxis = config.categoryAxis
        if (['bar', 'pie', 'donut', 'pictorialBar', 'radialBar', 'gauge'].includes(newSeriesType)) {
            if (newConfig.categoryAxis === 'Time') {
                if (
                    Array.isArray(chartState.drillAttributes) &&
                    chartState.drillAttributes.length > 0
                )
                    newConfig.categoryAxis = chartState.drillAttributes[0]
                newConfig.timeRange = 'Most Recent'
            }
        }

        if (
            !metricName ||
            newSeriesType === 'scatter' ||
            newSeriesType === 'bar' ||
            newConfig.seriesType === 'scatter' ||
            newConfig.seriesType === 'bar' ||
            newSeriesType === 'waterfall'
        ) {
            newConfig.seriesTypeMap.clear()
        } else {
            if (newConfig.seriesTypeMap.size < chartState?.result?.metrics?.length) {
                chartState?.result?.metrics.forEach((metric) => {
                    if (!newConfig.seriesTypeMap.has(metric.name))
                        newConfig.seriesTypeMap.set(metric.name, 'column')
                })
            }
            newConfig.seriesTypeMap.set(metricName, newSeriesType)
        }
        if ((newSeriesType === 'pie' || newSeriesType === 'donut') && newConfig.drillAttribute) {
            newConfig.categoryAxis = newConfig.drillAttribute
            newConfig.drillAttribute = null
        }
        setConfig(newConfig)
        chartState.configureSeries(newSeriesType, newConfig)
        const newCategoryAxis = newConfig.categoryAxis
        if (oldCategoryAxis !== newCategoryAxis) chartState.getResults(newConfig)
    }

    const handleChangeDrill = (event) => {
        let newDrillAttribute = event.target.value
        if (newDrillAttribute === 'None') newDrillAttribute = null
        config.drillAttribute = newDrillAttribute
        chartState.getResults(config)
        setConfig({ ...config })
    }

    const handleCategoryAxisChange = (e) => {
        const newConfig = { ...config }
        if (e.target.value === 'Time') newConfig.timeRange = 'Last Year'
        newConfig.categoryAxis = e.target.value
        setConfig(newConfig)
        chartState.getResults(newConfig)
    }

    const handleSortingChange = (target, value) => {
        const newConfig = cloneDeep(config)
        if (!newConfig.sortColumns || newConfig.sortColumns.length === 0)
            newConfig.sortColumns = [
                {
                    column: '',
                    order: SORTING.NAT,
                },
            ]
        newConfig.sortColumns[0][target] = value
        if (!newConfig.sortColumns?.[0].column)
            newConfig.sortColumns[0].column = buildSortingColumnOptions[0]
        newConfig.sortColumns = newConfig.sortColumns.filter(
            (column) => column.order !== SORTING.NAT
        )
        chartState.getResults(newConfig)
        setConfig(newConfig)
    }

    const handleTableTimeSeriesToggle = () => {
        const newConfig = { ...config }
        const timeSeries = !newConfig.tableTimeSeries
        newConfig.tableTimeSeries = timeSeries
        if (timeSeries) newConfig.timeRange = 'Last Year'
        else newConfig.timeRange = 'Most Recent'
        setConfig(newConfig)
    }

    const handleClearColumns = () => {
        const newConfig = { ...config }
        newConfig.selectedTableColumns = []
        setConfig(newConfig)
    }

    const handleOpenColumnMenu = (e, col) => {
        dateMenuRef.current = e.target
        setCurrentColumn(col)
        setColMenuOpen(true)
        if (workspaceMetadata) {
            workspaceMetadata?.inboxes?.forEach((inbox) => {
                inbox.tables.forEach((table) => {
                    if (table.reportSeriesTableID === col.reportSeriesTableID)
                        setCurrentTable(table)
                })
            })
        }
    }

    const handleCloseColumnMenu = () => {
        setColMenuOpen(false)
        setCurrentTable(null)
        setCurrentColumn(null)
    }

    const findCurrentColumn = () => {
        let changeColumn = null
        let changeColumnIndex = -1
        config.selectedTableColumns.forEach((col, index) => {
            if (
                col.columnName === currentColumn?.columnName &&
                col.reportSeriesTableID === currentTable?.reportSeriesTableID
            ) {
                changeColumn = col
                changeColumnIndex = index
            }
        })
        return { index: changeColumnIndex, column: changeColumn }
    }

    const handleByDateSelect = (value) => {
        const newConfig = { ...config }
        if (currentColumn.worksheetID) {
            const colIndex = newConfig.selectedTableColumns.findIndex(
                (c) =>
                    c.columnName === currentColumn.columnName &&
                    c.worksheetID === currentColumn.worksheetID
            )
            const worksheetCol = newConfig.selectedTableColumns[colIndex]
            newConfig.selectedTableColumns[colIndex] = {
                ...worksheetCol,
                byDate: value,
            }
        } else {
            const { index: changeColumnIndex, column: changeColumn } = findCurrentColumn()
            if (changeColumn && changeColumnIndex !== -1) {
                newConfig.selectedTableColumns[changeColumnIndex] = {
                    ...changeColumn,
                    byDate: value,
                }
            }
        }
        setConfig(newConfig)
        setDateFlag(!dateFlag)
        handleCloseColumnMenu()
    }

    const getByDateItems = () => {
        const { column } = findCurrentColumn()
        const snapshot = {
            selectedDate: 'Snapshot Date',
            reportSeriesTableID: currentColumn?.reportSeriesTableID,
            columnName: currentColumn?.columnName,
        }
        const items = []
        if (currentColumn?.worksheetID) {
            config.worksheetColumns
                .filter((c) => c.columnType === 'DateTime')
                .forEach((c) => {
                    items.push(
                        <ScoopMenuItem
                            key={c.columnName}
                            selected={column?.byDate?.selectedDate === c.columnName}
                            onClick={() => handleByDateSelect(c.columnName)}
                        >
                            by {c.columnName}
                        </ScoopMenuItem>
                    )
                })
        }
        if (currentTable && currentTable.dates) {
            items.push(
                <ScoopMenuItem
                    key="snapshot"
                    selected={!column?.byDate || column?.byDate?.selectedDate === 'Snapshot Date'}
                    onClick={() => handleByDateSelect(snapshot)}
                >
                    by Snapshot Date
                </ScoopMenuItem>
            )
            const byDates = currentTable.dates.map((d) => {
                const value = {
                    selectedDate: d,
                    reportSeriesTableID: currentTable.reportSeriesTableID,
                    columnName: currentColumn?.columnName,
                }
                return (
                    <ScoopMenuItem
                        key={d}
                        selected={column?.byDate?.selectedDate === d}
                        onClick={() => handleByDateSelect(value)}
                    >
                        by {d}
                    </ScoopMenuItem>
                )
            })
            items.push(...byDates)
        }
        return items
    }

    const handleChartTitleChange = (value) => {
        setChartTitle(value)
        debouncedFunc(value)
    }

    const handleKPITargetChange = (value) => {
        setKpiCompareTarget(value)
        targetDebouncedFunc(value)
    }

    const isTableColSelected = (col) => {
        if (col.isMeasure) {
            return config.selectedTableColumns.some(
                (c) =>
                    c.columnName === col.columnName &&
                    (col.reportSeriesTableID === c.reportSeriesTableID ||
                        col.worksheetID === c.worksheetID)
            )
        }
        return config.selectedTableColumns.some((c) => c.columnName === col.columnName)
    }

    const getTableColDisplayName = (col) => {
        if (col.isMeasure) {
            if (availableColumns.filter((c) => c.columnName === col.columnName).length > 1) {
                if (col.reportSeriesTableID) return `${col.columnName} (${col.tableName})`
                if (col.worksheetID) return `${col.columnName} (${col.rangeName})`
            }
        }
        return col.columnName
    }

    return (
        <div className={styles.configTabWrapper}>
            <div className={styles.viewSelectorContainer}>
                <ScoopSelector
                    value={config.view}
                    onChange={handleViewChange}
                    options={[
                        {
                            value: 'table',
                            label: 'Table',
                            leftSection: (
                                <div className={styles.selectIconWrapper}>
                                    <TableIcon size={16} />
                                </div>
                            ),
                        },
                        {
                            value: 'chart',
                            label: 'Chart',
                            leftSection: (
                                <div className={styles.selectIconWrapper}>
                                    <ChartBarIcon size={16} />
                                </div>
                            ),
                        },
                        {
                            value: 'kpi',
                            label: 'KPI',
                            leftSection: (
                                <div className={styles.selectIconWrapper}>
                                    <ChartLineUpIcon size={16} />
                                </div>
                            ),
                        },
                    ]}
                    size="md"
                    removeMargin
                />
            </div>
            <div className={styles.advancedSection}>
                <ScoopText className={styles.advancedLabel}>Advanced</ScoopText>
                <ScoopSwitch
                    checked={advanced}
                    onChange={handleAdvancedToggle}
                    disabled={
                        config.selectedItems?.length === 0 &&
                        config.selectedTableColumns.length === 0
                    }
                />
            </div>
            {(config.view === 'chart' || config.view === 'kpi') && (
                <div className={styles.configSection}>
                    <div className={styles.kpiTitleField}>
                        <ScoopTextField
                            value={chartTitle}
                            onChange={(e) => handleChartTitleChange(e.target.value)}
                            placeholder="Enter title..."
                            disableMargin
                            label={config.view === 'kpi' ? 'KPI' : 'Chart'}
                            title
                        />
                    </div>
                </div>
            )}
            {config.view === 'kpi' && (
                <div className={styles.configSection}>
                    <ScoopSelector
                        value={config.kpiCompareType}
                        onChange={(event) => {
                            const newConfig = { ...config }
                            newConfig.kpiCompareType = event.target.value
                            setConfig(newConfig)
                        }}
                        label={`${!config.tableTimeSeries ? 'Target' : 'Prior period'} comparison`}
                        labelClassName="selector-label-bold"
                        options={[
                            { value: 'percentage', label: 'Percentage' },
                            { value: 'number', label: 'Number' },
                            { value: 'none', label: 'None' },
                        ]}
                        size="sm"
                    />
                </div>
            )}
            {config.view !== 'kpi' && !config.worksheetID && (
                <ScoopSelector
                    value={config.displayRows}
                    onChange={(event) => {
                        const newConfig = cloneDeep(config)
                        newConfig.displayRows = event.target.value
                        if (config.view === 'chart') chartState.getResults(newConfig)
                        setConfig(newConfig)
                    }}
                    label="Display"
                    labelClassName="selector-label-bold"
                    options={[
                        { value: 5, label: 'Limit 5' },
                        { value: 10, label: 'Limit 10' },
                        { value: 50, label: 'Limit 50' },
                        { value: 100, label: 'Limit 100' },
                        { value: 500, label: 'Limit 500' },
                        { value: 'All', label: 'All' },
                    ]}
                    size="sm"
                />
            )}
            {config.view === 'table' && (
                <>
                    <div className={styles.columnsHeader}>
                        <ScoopText className={styles.columnsTitle}>Columns</ScoopText>
                        {columnLoading ? (
                            <ScoopSpinner size={24} />
                        ) : (
                            <ScoopButton
                                variant="text"
                                size="xxs"
                                onClick={handleClearColumns}
                                disabled={config.selectedTableColumns.length === 0}
                            >
                                <ArrowCounterClockwiseIcon
                                    size={20}
                                    className={styles.columnsResetIcon}
                                />
                            </ScoopButton>
                        )}
                    </div>
                    <div className={styles.columnsList}>
                        {(
                            config.worksheetID
                                ? config.worksheetColumns.length === 0
                                : availableColumns.length === 0
                        ) ? (
                            <ScoopText className={styles.noColumnsText}>
                                Select a KPI or Source to view available columns
                            </ScoopText>
                        ) : (
                            availableColumns
                                .filter((col) => col.columnName)
                                .sort((a, b) => a.columnName.localeCompare(b.columnName))
                                .map((col) => {
                                    const checked = isTableColSelected(col)
                                    return (
                                        <div key={col.columnName} className={styles.columnItem}>
                                            <ScoopCheckbox
                                                checked={checked}
                                                onChange={() => handleColumnToggle(col)}
                                                size="md"
                                            />
                                            <ScoopText className={styles.columnName}>
                                                {getTableColDisplayName(col)}
                                            </ScoopText>
                                            {col.isMeasure && checked && (
                                                <button
                                                    onClick={(e) => handleOpenColumnMenu(e, col)}
                                                    className={styles.columnMenuButton}
                                                >
                                                    <DotsThreeIcon size={16} weight="bold" />
                                                </button>
                                            )}
                                        </div>
                                    )
                                })
                        )}
                    </div>
                    <ScoopMenu
                        open={colMenuOpen}
                        anchorEl={dateMenuRef?.current}
                        onClose={handleCloseColumnMenu}
                        placement="bottom-end"
                    >
                        {getByDateItems()}
                    </ScoopMenu>
                </>
            )}
            {(config.view === 'table' || config.view === 'kpi') && (
                <>
                    <hr className={styles.divider} />
                    <div className={styles.timeSeriesSection}>
                        <ScoopText className={styles.timeSeriesLabel}>Time series</ScoopText>
                        <ScoopSwitch
                            disabled={config.disableTimeSeries}
                            checked={config.tableTimeSeries}
                            onChange={handleTableTimeSeriesToggle}
                        />
                    </div>
                </>
            )}
            {config.view === 'chart' &&
                (config.seriesType === 'bar' || config.seriesType === 'column') && (
                    <>
                        <hr className={styles.divider} />
                        <div className={styles.stackingSection}>
                            <div className={styles.stackingLabelContainer}>
                                <ScoopText className={styles.stackingLabel}>Stacking</ScoopText>
                                <ScoopInfoTooltip title="Stacking is only available for bar and column charts." />
                            </div>
                            <ScoopSwitch
                                checked={config.stacked}
                                onChange={(event) => {
                                    config.stacked = !config.stacked
                                    chartState.configureSeries(config.seriesType, config)
                                }}
                                disabled={config.selectedItems?.length === 0}
                            />
                        </div>
                        <hr className={styles.divider} />
                    </>
                )}
            {advanced && (
                <>
                    {config.view === 'chart' &&
                        chartState.result?.metrics?.map((metric) => {
                            const options = [
                                { value: 'column', label: 'Column' },
                                { value: 'line', label: 'Line' },
                                { value: 'area', label: 'Area' },
                                { value: 'bar', label: 'Bar' },
                            ]

                            if (config.selectedItems?.length <= 1) {
                                options.push({ value: 'pie', label: 'Pie' })
                                options.push({ value: 'donut', label: 'Donut' })
                                if (config.drillAttribute && config.categoryAxis !== 'Time') {
                                    options.push({ value: 'heatmap', label: 'Heatmap' })
                                }
                            }

                            options.push({ value: 'pictorialBar', label: 'Pictorial' })
                            options.push({ value: 'gauge', label: 'Gauge' })
                            options.push({ value: 'radialBar', label: 'Radial Bar' })

                            if (config.selectedItems?.length >= 2) {
                                options.push({ value: 'scatter', label: 'Scatter' })
                            }

                            if (config.worksheetID) {
                                options.push({ value: 'waterfall', label: 'Waterfall' })
                            }

                            return (
                                <ScoopSelector
                                    key={metric.name}
                                    value={
                                        config.seriesTypeMap.has(metric.name)
                                            ? config.seriesTypeMap.get(metric.name)
                                            : config.seriesType
                                    }
                                    onChange={(event) =>
                                        handleSeriesTypeChange(metric.name, event.target.value)
                                    }
                                    label={metric.name + ' Chart Type'}
                                    labelClassName="selector-label-bold"
                                    options={options}
                                    size="sm"
                                />
                            )
                        })}
                </>
            )}
            {!advanced && config.view === 'chart' && (
                <>
                    <ScoopSelector
                        value={config.seriesType}
                        onChange={(event) => handleSeriesTypeChange(null, event.target.value)}
                        label="Chart Type"
                        labelClassName="selector-label-bold"
                        options={(() => {
                            const options = [
                                { value: 'column', label: 'Column' },
                                { value: 'line', label: 'Line' },
                                { value: 'area', label: 'Area' },
                                { value: 'bar', label: 'Bar' },
                            ]

                            if (config.selectedItems?.length <= 1) {
                                options.push({ value: 'pie', label: 'Pie' })
                                options.push({ value: 'donut', label: 'Donut' })
                                if (config.drillAttribute && config.categoryAxis !== 'Time') {
                                    options.push({ value: 'heatmap', label: 'Heatmap' })
                                }
                            }

                            options.push({ value: 'pictorialBar', label: 'Pictorial' })
                            options.push({ value: 'gauge', label: 'Gauge' })
                            options.push({ value: 'radialBar', label: 'Radial Bar' })

                            if (config.selectedItems?.length >= 2) {
                                options.push({ value: 'scatter', label: 'Scatter' })
                            }

                            if (config.worksheetID) {
                                options.push({ value: 'waterfall', label: 'Waterfall' })
                            }

                            return options
                        })()}
                        size="sm"
                    />
                </>
            )}
            {advanced && config.view === 'chart' && (
                <ScoopSelector
                    value={config.categoryAxis}
                    onChange={handleCategoryAxisChange}
                    label="Category Axis"
                    labelClassName="selector-label-bold"
                    options={(() => {
                        const options = []

                        if (!['bar', 'pictorialBar', 'radialBar'].includes(config.seriesType)) {
                            options.push({ value: 'Time', label: 'Time' })
                        }

                        chartState.drillAttributes?.forEach((drillAttribute) => {
                            if (drillAttribute !== config.drillAttribute) {
                                options.push({ value: drillAttribute, label: drillAttribute })
                            }
                        })

                        if (!config.worksheetID) {
                            options.push({ value: 'Other', label: 'Other' })
                        }

                        return options
                    })()}
                    size="sm"
                />
            )}
            {advanced &&
                config.view === 'chart' &&
                config.categoryAxis !== 'Time' &&
                !['radial', 'gauge'].includes(config.seriesType) && (
                    <div className={styles.sortByContainer}>
                        <ScoopSelector
                            value={config.sortColumns?.[0]?.column || buildSortingColumnOptions[0]}
                            onChange={(e) => handleSortingChange('column', e.target.value)}
                            label="Sort By"
                            labelClassName="selector-label-bold"
                            options={buildSortingColumnOptions.map((col) => ({
                                value: col,
                                label: col,
                            }))}
                            size="sm"
                        />
                        <ScoopSelector
                            value={config.sortColumns?.[0]?.order || SORTING.NAT}
                            onChange={(e) => handleSortingChange('order', e.target.value)}
                            label="Sort Order"
                            labelClassName="selector-label-bold"
                            options={SORTING_OPTIONS.map((o) => ({
                                value: o.value,
                                label: o.label,
                            }))}
                            size="sm"
                        />
                    </div>
                )}
            {config.view === 'kpi' && !config.tableTimeSeries && (
                <div className={styles.configSection}>
                    <hr className={styles.divider} />
                    <div className={styles.kpiTargetField}>
                        <ScoopTextField
                            value={kpiCompareTarget}
                            onChange={(e) =>
                                handleKPITargetChange(e.target.value.replace(/\D/g, ''))
                            }
                            placeholder="Enter target value..."
                            disableMargin
                            label="Target value"
                        />
                    </div>
                </div>
            )}
            {config.selectedItems.length > 1 && config.view === 'chart' && (
                <ScoopButton
                    variant="secondary"
                    size="sm"
                    onClick={() => chartState.invertAxis()}
                    className={styles.invertAxisButton}
                >
                    Switch axis order
                </ScoopButton>
            )}
            {(config.view === 'kpi' ? config.tableTimeSeries : true) && (
                <div className={styles.configSection}>
                    <hr className={styles.divider} />
                    <TimeRangeControl
                        config={config}
                        setConfig={setConfig}
                        chartProperties={chartState}
                        onOpenTimeRange={onOpenTimeRange}
                        controlHeight={controlHeight}
                        controlWidth={'100%'}
                    />
                </div>
            )}
            {config.categoryAxis === 'Time' && config.tableTimeSeries && (
                <div className={styles.configSection}>
                    <ScoopSelector
                        value={config.period}
                        onChange={(event) => {
                            const newConfig = { ...config }
                            newConfig.period = event.target.value
                            if (config.view === 'chart') chartState.getResults(newConfig)
                            setConfig(newConfig)
                        }}
                        label="Period Frequency"
                        labelClassName="selector-label-bold"
                        options={[
                            { value: 'Daily', label: 'Daily' },
                            { value: 'Weekly', label: 'Weekly' },
                            { value: 'Monthly', label: 'Monthly' },
                            { value: 'Quarterly', label: 'Quarterly' },
                            { value: 'Annually', label: 'Annually' },
                        ]}
                        size="sm"
                    />
                </div>
            )}
            {config.categoryAxis === 'Time' && config.tableTimeSeries && (
                <div className={styles.configSection}>
                    <ScoopSelector
                        value={config.calendarType}
                        onChange={(event) => {
                            config.calendarType = event.target.value
                            if (config.selectedItems?.length === 0) {
                                chartState.series = null
                            } else {
                                config.calendarType = event.target.value
                                chartState.getResults(config)
                            }
                            setConfig({ ...config })
                        }}
                        label="Period End"
                        labelClassName="selector-label-bold"
                        options={[
                            { value: 'Rolling', label: 'Rolling' },
                            { value: 'Calendar', label: 'Calendar' },
                        ]}
                        size="sm"
                    />
                </div>
            )}
            {!['scatter', 'pie', 'donut', 'pictorialBar', 'radialBar'].includes(
                config.seriesType
            ) &&
                config.view === 'chart' && (
                    <>
                        <hr className={styles.divider} />
                        <ScoopSelector
                            value={config.drillAttribute ? config.drillAttribute : 'None'}
                            onChange={handleChangeDrill}
                            label="Group By"
                            labelClassName="selector-label-bold"
                            options={(() => {
                                const options = [{ value: 'None', label: 'None' }]

                                chartState.drillAttributes?.forEach((item) => {
                                    if (item !== config.categoryAxis) {
                                        options.push({ value: item, label: item })
                                    }
                                })

                                return options
                            })()}
                            size="sm"
                        />
                    </>
                )}
            {advanced && config.view !== 'kpi' && (
                <div className={styles.analyzeChangesSection}>
                    <div className={styles.analyzeChangesLabelContainer}>
                        <ScoopText className={styles.analyzeChangesLabel}>
                            Analyze Changes
                        </ScoopText>
                        <ScoopInfoTooltip title="Allow drilling and grouping by snapshot changes." />
                    </div>
                    <ScoopSwitch
                        checked={analyzeChanges}
                        onChange={(event) => {
                            setAnalyzeChanges(!analyzeChanges)
                        }}
                    />
                </div>
            )}
        </div>
    )
}
